"use client";
import React, { useId, useState } from "react";
import Link from "next/link";
import Button from "../ui/Button";
import ProgramActions from "./ProgramActions";
import FinanceablePill from "./FinanceablePill";
import {
  Clock3,
  Euro,
  GraduationCap,
  Users,
  Star,
} from "lucide-react";
import type { IFormationDetail } from "@/types/formation";
import { MODALITY_LABELS } from "@/constants/formation";
import { formatEuro } from "@/lib/utils";

type PricingTab = "inter" | "intra" | "sur-mesure";

const PRICING_TABS: { key: PricingTab; label: string }[] = [
  { key: "inter", label: "Inter" },
  { key: "intra", label: "Intra" },
  { key: "sur-mesure", label: "Sur-mesure" },
];

/** The card's CTAs — one height (48px) and one measure across all three tabs. */
const CTA_CLASS = "w-full h-12 rounded-lg text-sm px-6 font-bold";

const DetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}> = ({ icon, label, value, valueClassName = "" }) => (
  <div className="flex items-center justify-between gap-3 py-3 border-b border-[#EFEFEF] last:border-b-0">
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-sm font-semibold text-[#1B365E]">{label}</span>
    </div>
    <span
      className={`text-sm font-medium text-[#1B365E] text-right ${valueClassName}`}
    >
      {value}
    </span>
  </div>
);

interface PriceCardProps {
  formation: IFormationDetail;
  /** Extra classes for the outer wrapper — controls order / width / responsive show-hide. */
  className?: string;
}

const PriceCard: React.FC<PriceCardProps> = ({ formation, className = "" }) => {
  const [activeTab, setActiveTab] = useState<PricingTab>("inter");

  /* The card is mounted twice per page — inline in the hero below lg, and in the
     desktop rail — so the tab/panel ids have to be per-instance or the two sets
     collide and aria-controls points at the wrong card. */
  const uid = useId();
  const tabId = (key: PricingTab) => `${uid}-tab-${key}`;
  const panelId = (key: PricingTab) => `${uid}-panel-${key}`;

  /* Arrow keys move between the pricing tabs, as a tablist is expected to. */
  const handleTabKeys = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const offset =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (offset === 0) return;

    event.preventDefault();
    const current = PRICING_TABS.findIndex((tab) => tab.key === activeTab);
    const next = PRICING_TABS[(current + offset + PRICING_TABS.length) % PRICING_TABS.length];
    setActiveTab(next.key);
    document.getElementById(tabId(next.key))?.focus();
  };

  const panelProps = (key: PricingTab) => ({
    id: panelId(key),
    role: "tabpanel" as const,
    "aria-labelledby": tabId(key),
    tabIndex: 0,
  });

  const formatLabel = formation.modality?.length
    ? formation.modality.map((m) => MODALITY_LABELS[m] ?? m).join(" / ")
    : "Présentiel";

  const isFinanceable = formation.is_financeable;

  // Tarifs affichés HT : la cible est professionnelle (entreprises / indépendants).
  const individualPrice = formation.pricing.individual.price_ht;
  const groupPrice = formation.pricing.group.price_ht;
  const groupCapacity =
    formation.sessions.find((s) => s.capacity)?.capacity ?? null;

  const durationIcon = <GraduationCap className="w-5 h-5 text-[#40BFDE] shrink-0" />;
  const clockIcon = <Clock3 className="w-5 h-5 text-[#40BFDE] shrink-0" />;
  const euroIcon = <Euro className="w-5 h-5 text-[#40BFDE] shrink-0" />;
  const usersIcon = <Users className="w-5 h-5 text-[#40BFDE] shrink-0" />;

  return (
    <div
      className={`bg-white rounded-3xl border border-[#ABABAB]/30 shadow-[1px_2px_8px_2px_#0000001A] overflow-hidden ${className}`}
    >
      {/* Pricing tabs. The active one was `bg-[#fffff]` — a five-digit hex, so
          no rule was emitted at all and the state relied on the card behind it. */}
      <div
        role="tablist"
        aria-label="Formules tarifaires"
        onKeyDown={handleTabKeys}
        className="flex"
      >
        {PRICING_TABS.map(({ key, label }) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              id={tabId(key)}
              type="button"
              role="tab"
              onClick={() => setActiveTab(key)}
              aria-selected={isActive}
              aria-controls={panelId(key)}
              tabIndex={isActive ? 0 : -1}
              className={`flex-1 py-4 text-sm font-bold transition-colors cursor-pointer ${
                isActive
                  ? "bg-white text-[#1B365E]"
                  : "bg-[#C7F8FF] text-[#1B365E]/70 hover:bg-[#b3f1fc]"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="px-6 py-6">
        {/* Inter */}
        {activeTab === "inter" && (
          <div {...panelProps("inter")}>
            <div className="flex flex-col">
              <DetailRow icon={durationIcon} label="Format" value={formatLabel} />
              <DetailRow
                icon={clockIcon}
                label="Durée totale"
                value={
                  <>
                    {formation.duration_days ?? "—"} jours • {formation.duration_hours} heures
                  </>
                }
              />
              <DetailRow
                icon={euroIcon}
                label="Prix"
                value={
                  individualPrice !== null
                    ? `${formatEuro(individualPrice)} HT`
                    : "Sur devis"
                }
                valueClassName="text-lg font-bold"
              />
            </div>

            {isFinanceable && (
              <div className="flex justify-center mt-4">
                <FinanceablePill />
              </div>
            )}

            {/* Both CTAs are links: one used to be a Button inside a Link, the
                other a Link inside a Button — nested interactive elements with
                two different hit areas. */}
            <div className="flex flex-col gap-3 mt-5">
              <Button
                href="#sessions"
                variant="gradient"
                size="unset"
                className={CTA_CLASS}
              >
                S&apos;inscrire à la formation
              </Button>
              <Button
                href="#sessions"
                variant="ghost"
                size="unset"
                className={CTA_CLASS}
              >
                Consulter nos dates et lieux
              </Button>
            </div>
          </div>
        )}

        {/* Intra */}
        {activeTab === "intra" && (
          <div {...panelProps("intra")}>
            <div className="flex flex-col">
              <DetailRow icon={durationIcon} label="Format" value={formatLabel} />
              <DetailRow
                icon={clockIcon}
                label="Durée totale"
                value={
                  <>
                    {formation.duration_days ?? "—"} jours • {formation.duration_hours} heures
                  </>
                }
              />
              <DetailRow
                icon={euroIcon}
                label="Prix"
                value={
                  groupPrice !== null ? `${formatEuro(groupPrice)} HT` : "Sur devis"
                }
                valueClassName="text-lg font-bold"
              />
              {groupCapacity !== null && (
                <DetailRow
                  icon={usersIcon}
                  label="Groupe"
                  value={`${groupCapacity} personnes`}
                />
              )}
            </div>

            {isFinanceable && (
              <div className="flex justify-center mt-4">
                <FinanceablePill />
              </div>
            )}

            <div className="flex flex-col gap-3 mt-5">
              <Button variant="gradient" size="unset" href="#devis" className={CTA_CLASS}>
                Demander un devis
              </Button>
            </div>
          </div>
        )}

        {/* Sur-mesure */}
        {activeTab === "sur-mesure" && (
          <div {...panelProps("sur-mesure")}>
            <p className="text-[#1B365E] text-sm font-medium leading-6 text-center">
              Vous souhaitez une formation sur-mesure ou vous disposez
              d&apos;un cahier des charges ?
            </p>
            <div className="flex flex-col gap-3 mt-5">
              <Button variant="gradient" size="unset" href="#devis" className={CTA_CLASS}>
                Déposer un cahier des charges
              </Button>
              <Button variant="ghost" size="unset" href="#devis" className={CTA_CLASS}>
                Demander un devis
              </Button>
            </div>
          </div>
        )}

        {/* Actions — save, print, download & share the programme, plus the rating badge.
            Shared across all three tabs: these describe the formation, not the pricing plan. */}
        <div className="flex items-center justify-center gap-2 mt-5 pt-5 border-t border-[#EFEFEF]">
          <ProgramActions formation={formation} />
          <Link
            href="#évaluations"
            aria-label={
              formation.rating_avg !== null
                ? `Note moyenne : ${formation.rating_avg.toFixed(1)} sur 5 (${formation.reviews_count} avis) — voir les avis`
                : "Aucun avis pour le moment — voir les avis"
            }
            title={`${formation.reviews_count} avis`}
            /* 46px, matching the four round actions it shares this row with —
               it used to be 41px, so the row sat unevenly. */
            className="
              inline-flex items-center justify-center gap-1
              h-11.5 shrink-0 px-3.5
              rounded-xl bg-white
              border border-[#FFBC42]
              hover:bg-[#fff5e6] hover:scale-110
              transition duration-200
              z-10
            "
          >
            <Star size={18} className="text-[#FFBC42] fill-[#FFBC42]" aria-hidden="true" />
            <span className="text-sm font-bold text-[#fc7f16] leading-none">
              {formation.rating_avg !== null ? formation.rating_avg.toFixed(1) : "—"}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PriceCard;
